import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import ContactSearchAutoComplete from "components/common/ContactSearchAutoComplete";
import ContactStatus from "enums/ContactStatus";
import { useEffect } from "react";
import CurrencyInput from "../common/CurrencyInput";
import dayjs from "dayjs";
import { HugeiconsIcon } from "@hugeicons/react";
import { ImageAdd01Icon } from "@hugeicons-pro/core-stroke-rounded";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import PaymentPaymentTypes from "enums/PaymentPaymentTypes";

import { paymentMethodLabelKey } from "utils/payments/paymentMethod";
import {
  formatPayToForPayment,
  FormikPayment,
} from "utils/payments/paymentFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { BankAccount } from "hooks/useGetBankAccounts";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import FormikCustomFields from "components/common/FormikCustomFields";
import { useFeatureEntitlements } from "@frontegg/react";
import { FormikCustomField } from "hooks/customFields.types";
import AllocationSearchAutoComplete from "components/common/AllocationSearchAutoComplete";
import ExpenseCategorySearchAutoComplete from "components/common/CategorySearchAutoComplete";
import { DatePicker, FormField, Select, TextField } from "@outpave/ds";
import PanelSection from "components/common/PanelSection";
import { Inline, Stack } from "components/layout/reusableLayouts";

/** Only these two reach the payments API; the enum carries two it never sends. */
const PAYMENT_METHOD_OPTIONS = [
  PaymentPaymentTypes.ACH,
  PaymentPaymentTypes.WIRE,
];

interface EditPaymentDetailsProps {
  formikPayment: FormikProps<FormikPayment>;
  bankAccountData?: BankAccount | null;
  loadingBankAccountDetails?: boolean;
  paymentContactMissingBankErrorMessage: React.ReactNode | null;
  refetchSelectedPayToContactIndex: number;
  handleLocalFileUpload: (newAssets: LocalImageAsset[]) => void;
  handleRemoveAttachments: (assetId: string) => void;
  customFieldSettings?: FormikCustomField[];
}

const EditPaymentDetails = (props: EditPaymentDetailsProps) => {
  const {
    formikPayment,
    bankAccountData,
    loadingBankAccountDetails,
    paymentContactMissingBankErrorMessage,
    refetchSelectedPayToContactIndex,
    handleLocalFileUpload,
    handleRemoveAttachments,
    customFieldSettings,
  } = props;
  const { t } = useTranslation();
  const today = dayjs();

  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const { handleFormikHelperText, handleFormikError } =
    useFormikHelpers(formikPayment);

  function getBankAccountDisplay(account?: BankAccount | null) {
    if (!account) return "";
    const last4 = account.last4 || account.accountNumber?.slice(-4) || "";
    const name = account.nickname || account.bankName || "";
    return `${name} ••••${last4}`;
  }

  const bankAccountDisplay = getBankAccountDisplay(bankAccountData);
  const PayFromValue = loadingBankAccountDetails
    ? t("Loading Bank Account")
    : bankAccountDisplay;

  useEffect(() => {
    // Update if editing an existing payment and date is in the past
    if (
      formikPayment.values.scheduledPaymentDate &&
      dayjs(formikPayment.values.scheduledPaymentDate).isBefore(dayjs(), "day")
    ) {
      formikPayment.setFieldValue("scheduledPaymentDate", dayjs().toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikPayment.values.id, formikPayment.values.scheduledPaymentDate]);

  return (
    <Stack gap="var(--space-7)">
      <FormField label={t("Pay From")} fullWidth>
        <Inline gap="var(--space-3)" align="start" wrap={false}>
          <Select
            value={
              formikPayment.values.paymentMethod || PaymentPaymentTypes.ACH
            }
            onChange={(method) =>
              formikPayment.setFieldValue("paymentMethod", method)
            }
            options={PAYMENT_METHOD_OPTIONS.map((method) => ({
              id: method,
              label: t(paymentMethodLabelKey(method) || method),
            }))}
            aria-label={t("Payment Method")}
            required
            width={140}
          />
          <TextField
            name="payFrom"
            value={PayFromValue}
            readOnly
            fullWidth
            aria-label={t("Pay From")}
          />
        </Inline>
      </FormField>

      <ContactSearchAutoComplete
        onSelectContact={(recipient) => {
          const currentPayToId = formikPayment.values.payTo?.id;

          if (recipient && !Array.isArray(recipient)) {
            if (recipient.id !== currentPayToId) {
              formikPayment.setFieldValue(
                "payTo",
                formatPayToForPayment(recipient),
              );
            }
          } else if (!recipient && currentPayToId) {
            formikPayment.setFieldValue("payTo", formatPayToForPayment(null));
          }
        }}
        contactId={formikPayment.values.payTo?.id}
        customPlaceholder={t("Pay To")}
        status={ContactStatus.Active}
        showAddContactInDropdown={true}
        handleBlur={() => formikPayment.setFieldTouched("payTo.id", true)}
        errorText={
          paymentContactMissingBankErrorMessage ||
          handleFormikHelperText("payTo.id")
        }
        forcePickBusiness={true}
        refetchIndex={refetchSelectedPayToContactIndex}
      />

      <Inline gap="var(--space-4)" align="start" wrap={false}>
        <CurrencyInput
          label={t("Payment Amount")}
          name="amount"
          value={formikPayment.values.amount}
          fullWidth
          required={true}
          onChange={(raw) => formikPayment.setFieldValue("amount", raw)}
          onBlur={formikPayment.handleBlur}
          error={handleFormikError("amount")}
          helperText={handleFormikHelperText("amount")}
        />

        <FormField label={t("Due Date")} fullWidth>
          <DatePicker
            value={
              formikPayment.values.dueDate
                ? dayjs(formikPayment.values.dueDate)
                : null
            }
            clearable
            onChange={(date) => {
              const nextDate = date ? date.toDate() : null;
              formikPayment.setFieldValue("dueDate", nextDate);
              if (!formikPayment.values.scheduledPaymentDate) {
                formikPayment.setFieldValue("scheduledPaymentDate", nextDate);
              }
            }}
          />
        </FormField>
      </Inline>

      <Inline gap="var(--space-4)" align="start" wrap={false}>
        <FormField
          label={t("Payment Date")}
          required
          fullWidth
          tooltip={t("PaymentScheduledPaymentDateTooltip")}
          error={Boolean(handleFormikError("scheduledPaymentDate"))}
          errorText={
            handleFormikHelperText("scheduledPaymentDate") || undefined
          }
          helperText={t("Select the date you would like this payment sent")}
        >
          <DatePicker
            value={
              formikPayment.values.scheduledPaymentDate
                ? dayjs(formikPayment.values.scheduledPaymentDate)
                : null
            }
            minDate={today}
            clearable
            error={Boolean(handleFormikError("scheduledPaymentDate"))}
            onBlur={() =>
              formikPayment.setFieldTouched("scheduledPaymentDate", true)
            }
            onChange={(date) =>
              formikPayment.setFieldValue(
                "scheduledPaymentDate",
                date ? date.toDate() : null,
              )
            }
          />
        </FormField>
      </Inline>

      <ExpenseCategorySearchAutoComplete
        title={t("Category")}
        expenseCategory={formikPayment.values.expenseCategory || null}
        onSelectExpenseCategory={(expenseCategory) => {
          formikPayment.setFieldValue("expenseCategory", expenseCategory);
        }}
        hideClearButton={true}
      />

      <AllocationSearchAutoComplete
        allocation={formikPayment.values.allocationCode || null}
        allocationType={formikPayment.values.allocationType}
        onSelectAllocation={(allocation) => {
          formikPayment.setFieldValue("allocationCode", {
            id: allocation?.id,
            allocationCode: allocation?.allocationCode,
          });
          formikPayment.setFieldValue(
            "allocationType",
            allocation?.allocationType,
          );
        }}
      />

      {featureCustomFields && (
        <FormikCustomFields
          settings={customFieldSettings}
          formik={formikPayment}
          variant="flat"
        />
      )}

      <PanelSection
        title={t("Attachments")}
        icon={<HugeiconsIcon icon={ImageAdd01Icon} size={16} />}
      >
        <AttachmentsComponent
          renderType={AttachmentRenderType.ACCORDION}
          drawerType={AttachmentDrawerType.ATTACHMENT}
          localAssets={formikPayment.values.attachments}
          parentDetails={{
            parentId: "",
            parentType: ATTACHMENT_PARENT_TYPE.PAYMENT,
          }}
          onRemoveAssets={handleRemoveAttachments}
          onAddAssets={handleLocalFileUpload}
        />
      </PanelSection>
    </Stack>
  );
};

export default EditPaymentDetails;
